import { flatten, keys, values } from 'lodash';
import { markReturnAction, boundingPolyPrecisionAdjustReplacer } from '@utils';

import callApi from '@helpers/callApi';
import { SAVE_MEDIA_DATA_FAILURE, SAVE_MEDIA_DATA_SUCCESS } from '../actions';
import { SaveMediaDataRequest } from '../models';

import { udrsStoreToAssetAdapter } from './adapters/optical-tracking';
import { Thunk } from './interfaces';

export const MEDIA_DATA_VERSION = '2.0.0';

interface SaveMediaDataResponse {
  createAsset: {
    id: string;
    createdDateTime: string;
    container: {
      id: string;
      modifiedDateTime: string;
    };
  };
}
export const saveMediaData: Thunk<
  SaveMediaDataRequest,
  SaveMediaDataResponse
> =
  ({
    tdoId,
    email,
    checkpoint,
    rev,
    selectedPolyGroups,
    boundingPolys,
    clusterMap,
    audioRedactions,
    voiceMaskEdits,
    backgroundBlurEdits,
  }) =>
  // @ts-expect-error TODO: Fix types here
  async (dispatch, getState) => {
    const file = {
      tdoId,
      ...udrsStoreToAssetAdapter({ boundingPolys }),
      selectedPolyGroups,
      clusterMap,
      version: MEDIA_DATA_VERSION,
      audioRedactions,
      voiceMaskEdits,
      backgroundBlurEdits,
    };
    const details = {
      boundingPolysCount: flatten(values(boundingPolys)).reduce(
        (t, b) => t + (b?.series.length ?? 0),
        0
      ),
      selectedPolyGroupsCount: keys(selectedPolyGroups).length,
      audioRedactions: audioRedactions.length,
      voiceMaskCount: voiceMaskEdits?.length,
      backgroundBlurCount: backgroundBlurEdits?.length,
      email,
      ...(typeof checkpoint === 'number' && { checkpoint }),
      // VE-25870: an ordinary save, not sent for burn-in. Redact File flips this
      // to true and adds the version the output will carry (see
      // `markRedactionDataBurnedIn`), which is how the staleness check tells a
      // save that was burned in from one that never was. Written on every save,
      // including as `false`, because the key being ABSENT means something else
      // again: an asset written before this scheme existed, judgeable only by
      // timestamp. Absent, false and true are three distinct answers.
      burnInRequested: false,
    };

    // decimalPrecisionSavingBoundingPoly(file.boundingPolys);
    const query = `
      mutation($id: ID!, $details: JSONData!) {
      createAsset(input: {
          containerId: $id,
          type: "redaction-media-data",
          contentType: "application/json",
          details: $details
        }) {
          id
          createdDateTime
          # VE-25870: returned so the main thread can add this save to the
          # staleness comparison without refetching the TDO. The size gates the
          # legacy timestamp fallback, and without it here a save made in this
          # session would leave the file unchecked until the next full fetch.
          details
          fileData {
            size
          }
          container {
            id
            modifiedDateTime
          }
        }
      }
    `;

    return await callApi<{
      createAsset: {
        id: string;
        createdDateTime: string;
        fileData?: { size?: number | null };
        container: {
          id: string;
          modifiedDateTime: string;
        };
      };
    }>({
      actionTypes: ['NOOP', SAVE_MEDIA_DATA_SUCCESS, SAVE_MEDIA_DATA_FAILURE],
      query,
      variables: { id: tdoId, details },
      // VE-27765: stamp the rev this snapshot was taken at onto the success, so
      // the main thread can tell whether the editor was edited during upload.
      dispatch: (a: any) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        dispatch(
          markReturnAction(
            SAVE_MEDIA_DATA_SUCCESS.match(a)
              ? { ...a, meta: { ...a.meta, savedRev: rev } }
              : a
          )
        ),
      getState,
      bailout: undefined,
      file: JSON.stringify(file, boundingPolyPrecisionAdjustReplacer),
      body: undefined,
      operationName: 'saveUdrs',
      url: undefined,
    });
  };
